// Dashboard principale con KPI, mappa, grafici e alert in real-time
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../components/layout/PageHeader'
import { KPICards } from './components/KPICards'
import { TicketStatusChart } from './components/TicketStatusChart'
import { TechnicianMap } from './components/TechnicianMap'
import { RecentTickets } from './components/RecentTickets'
import { SLAGauge } from './components/SLAGauge'
import { AlertsPanel } from './components/AlertsPanel'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import api from '../../lib/api'
import { useRealtime } from '../../hooks/useRealtime'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { SOCKET_EVENTS } from '../../lib/socket'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import type { Ticket } from '@fieldcore/types'

// Shape reale della risposta /api/analytics/dashboard
interface OperationalDashboard {
  tickets: {
    open: number
    inProgress: number
    overdue: number
    closedToday: number
  }
  activeTechnicians: {
    total: number
    items: Array<{ id: string; firstName: string; lastName: string; lastLat: number; lastLng: number; lastSeen: string }>
  }
  sla: {
    respected: number
    violated: number
    complianceRatio: number
  }
  criticalAlerts: {
    criticalOpen: number
    recentBreaches: number
  }
}

// Adatta la risposta backend alla shape attesa dai sub-componenti
function adaptDashboardData(raw: OperationalDashboard) {
  return {
    kpi: {
      open: raw.tickets?.open ?? 0,
      inProgress: raw.tickets?.inProgress ?? 0,
      expired: raw.tickets?.overdue ?? 0,
      closedToday: raw.tickets?.closedToday ?? 0,
    },
    statusDistribution: [
      { status: 'Aperti', count: raw.tickets?.open ?? 0, color: '#0F52BA' },
      { status: 'In lavorazione', count: raw.tickets?.inProgress ?? 0, color: '#F59E0B' },
      { status: 'Scaduti', count: raw.tickets?.overdue ?? 0, color: '#EF4444' },
      { status: 'Chiusi oggi', count: raw.tickets?.closedToday ?? 0, color: '#10B981' },
    ],
    slaCompliance: raw.sla?.complianceRatio ?? 0,
    recentTickets: [],
    // AlertsPanel si aspetta DashboardAlert con ticketId, ticketNumber, title, severity, type, createdAt
    // Generiamo placeholder alert dalla risposta aggregata del backend
    alerts: raw.criticalAlerts
      ? [
          ...(raw.criticalAlerts.criticalOpen > 0
            ? [{
                id: 'c1',
                type: 'escalated' as const,
                ticketId: '',
                ticketNumber: `${raw.criticalAlerts.criticalOpen}`,
                title: `${raw.criticalAlerts.criticalOpen} ticket critici aperti`,
                severity: 'critical' as const,
                createdAt: new Date().toISOString(),
              }]
            : []),
          ...(raw.criticalAlerts.recentBreaches > 0
            ? [{
                id: 'c2',
                type: 'sla_breach' as const,
                ticketId: '',
                ticketNumber: `${raw.criticalAlerts.recentBreaches}`,
                title: `${raw.criticalAlerts.recentBreaches} breach SLA recenti`,
                severity: 'high' as const,
                createdAt: new Date().toISOString(),
              }]
            : []),
        ]
      : [],
  }
}

// -----------------------------------------------------------------------
// Vista personale per il tecnico
// -----------------------------------------------------------------------
function TechnicianDashboard() {
  const navigate = useNavigate()

  const { data: allTickets = [] } = useQuery<Ticket[]>({
    queryKey: ['tickets-tech-dashboard'],
    queryFn: async () => {
      const res = await api.get<{ items: Ticket[] }>('/tickets', { params: { pageSize: 200 } })
      return res.data.items ?? []
    },
    refetchInterval: 60 * 1000,
  })

  const today = new Date().toISOString().split('T')[0]!
  const firstOfMonth = today.slice(0, 7) + '-01'

  const open = allTickets.filter(t => ['new', 'assigned'].includes(t.status))
  const inProgress = allTickets.filter(t => t.status === 'in_progress')
  const completedMonth = allTickets.filter(t => t.status === 'completed' && t.updatedAt >= firstOfMonth)
  const urgent = allTickets.filter(t => ['critical', 'high'].includes(t.priority) && !['completed', 'cancelled'].includes(t.status))
  const recent = [...allTickets].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 8)

  const kpiCards = [
    { label: 'Assegnati', value: open.length, color: 'text-blue-600 bg-blue-50' },
    { label: 'In lavorazione', value: inProgress.length, color: 'text-amber-600 bg-amber-50' },
    { label: 'Completati (mese)', value: completedMonth.length, color: 'text-green-600 bg-green-50' },
    { label: 'Urgenti aperti', value: urgent.length, color: 'text-red-600 bg-red-50' },
  ]

  const STATUS_IT: Record<string, string> = {
    new: 'Nuovo', assigned: 'Assegnato', in_progress: 'In lavorazione',
    paused: 'In pausa', completed: 'Completato', cancelled: 'Annullato', escalated: 'Escalato',
  }
  const STATUS_COLOR: Record<string, string> = {
    new: 'bg-gray-100 text-gray-700', assigned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700', paused: 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700', escalated: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-400',
  }

  return (
    <div className="space-y-6">
      <PageHeader title="La mia dashboard" subtitle="I tuoi interventi in tempo reale" />

      {/* KPI */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {kpiCards.map(k => (
          <div key={k.label} className={`rounded-xl p-4 ${k.color}`}>
            <p className="text-2xl font-bold">{k.value}</p>
            <p className="text-sm font-medium mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Lista ticket recenti */}
      <div className="rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)]">
        <div className="px-4 py-3 border-b border-[var(--fc-border)] flex items-center justify-between">
          <h2 className="font-semibold text-[var(--fc-foreground)]">I miei interventi recenti</h2>
          <button onClick={() => navigate('/tickets')} className="text-sm text-[var(--fc-primary)] hover:underline">
            Vedi tutti →
          </button>
        </div>
        {recent.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fc-foreground-muted)]">Nessun ticket assegnato.</p>
        ) : (
          <div className="divide-y divide-[var(--fc-border)]">
            {recent.map(t => (
              <button
                key={t.id}
                onClick={() => navigate(`/tickets/${t.id}`)}
                className="w-full text-left px-4 py-3 hover:bg-[var(--fc-surface-overlay)] transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--fc-foreground)] truncate">{t.title}</p>
                    <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">{t.ticketNumber}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[t.status] ?? 'bg-gray-100 text-gray-700'}`}>
                    {STATUS_IT[t.status] ?? t.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { subscribe } = useRealtime()

  // Tecnico → vista personale
  if (user?.role === 'technician') return <TechnicianDashboard />

  const { data: rawData, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: OperationalDashboard }>('/analytics/dashboard')
      return response.data
    },
    refetchInterval: 60 * 1000, // refresh ogni minuto
  })

  const data = rawData?.data ? adaptDashboardData(rawData.data) : undefined

  // Aggiorna dashboard real-time quando arrivano nuovi ticket
  useEffect(() => {
    const unsub1 = subscribe(SOCKET_EVENTS.TICKET_CREATED, () => {
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    })
    const unsub2 = subscribe(SOCKET_EVENTS.TICKET_STATUS_CHANGED, () => {
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    })
    const unsub3 = subscribe(SOCKET_EVENTS.SLA_BREACH, () => {
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    })
    return () => {
      unsub1()
      unsub2()
      unsub3()
    }
  }, [subscribe, qc])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Panoramica operazioni in tempo reale"
      />

      {/* KPI Cards */}
      <KPICards data={data?.kpi} isLoading={isLoading} />

      {/* Riga 2: Mappa + Grafico stati */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <TechnicianMap />
        </div>
        <div>
          <TicketStatusChart data={data?.statusDistribution} />
        </div>
      </div>

      {/* Riga 3: Ticket recenti + SLA + Alert */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RecentTickets
            tickets={data?.recentTickets as Parameters<typeof RecentTickets>[0]['tickets']}
            isLoading={isLoading}
          />
        </div>
        <div className="space-y-6">
          <SLAGauge compliance={data?.slaCompliance} isLoading={isLoading} />
          <AlertsPanel alerts={data?.alerts as Parameters<typeof AlertsPanel>[0]['alerts']} />
        </div>
      </div>

      <ContextualHelp />
    </div>
  )
}
